import type { ReactNode } from 'react';

import { SITE } from '@/config';
import { locales } from '@/i18n';
import { useLocale, useT } from '@/i18n/LocaleProvider';

export type TopBarProps = {
  query: string;
  onQueryChange: (query: string) => void;
  brandHref?: string;
  onCreatePage?: () => void;
  onNavigateHome: () => void;
  // onSelectLanguage switches both the UI locale and the content language and
  // navigates to the current page's translation. When omitted, the switcher only
  // changes the UI chrome locale.
  onSelectLanguage?: (code: string) => void;
  personalTools: ReactNode;
};

export function TopBar({
  query,
  onQueryChange,
  brandHref = '/',
  onCreatePage,
  onNavigateHome,
  onSelectLanguage,
  personalTools,
}: TopBarProps) {
  const t = useT();
  const { locale, setLocale } = useLocale();
  const changeLanguage = onSelectLanguage ?? setLocale;

  return (
    <header className="global-bar">
      <a
        className="brand"
        href={brandHref}
        aria-label={SITE.title}
        onClick={(event) => {
          event.preventDefault();
          onNavigateHome();
        }}
      >
        <span>{SITE.brandPrimary}</span>
        <strong>{SITE.brandSecondary}</strong>
      </a>

      <label className="search-box">
        <span>{t('search_placeholder')}</span>
        <input
          value={query}
          type="search"
          autoComplete="off"
          onChange={(event) => onQueryChange(event.target.value)}
        />
      </label>

      <div className="lang-switcher">
        <span className="lang-switcher-icon" aria-hidden="true">
          🌐
        </span>
        <select
          aria-label={t('lang_switcher_label')}
          className="lang-switcher-select"
          value={locale}
          onChange={(event) => changeLanguage(event.target.value)}
        >
          {locales.map((entry) => (
            <option key={entry.code} value={entry.code}>
              {entry.label}
            </option>
          ))}
        </select>
        <span className="lang-switcher-caret" aria-hidden="true">
          ▾
        </span>
      </div>

      {onCreatePage ? (
        <button type="button" className="create-page-btn" onClick={onCreatePage}>
          {t('nav_create_page')}
        </button>
      ) : null}

      {personalTools}
    </header>
  );
}
